import React, { createContext, useEffect, useState } from "react"
import { useAuthContext } from "./AuthContextWrapper"
import { estateFormsApi } from "./apis"
import { AtticusForm } from "models/AtticusForm"

interface Props {
  children: JSX.Element;
}

export interface EstateContextInterface {
  estateForms?: AtticusForm[];
  estateFormsIsLoading: boolean;
  fetchEstateForms: (estateId: number) => void;
  addEstateForm: (estateId: number, userId: number, form: AtticusForm) => Promise<boolean>;
  removeEstateForm: (estateId: number, userId: number, form: AtticusForm) => Promise<boolean>;
}

export const EstateFormsContext = createContext<EstateContextInterface>({
  estateForms: undefined,
  estateFormsIsLoading: false,
  fetchEstateForms: () => undefined,
  addEstateForm: () => Promise.resolve(false),
  removeEstateForm: () => Promise.resolve(false),
});

export const EstateFormsContextWrapper = ({ children }: Props): JSX.Element => {
  const { activeEstate } = useAuthContext()
  const [estateForms, setEstateForms] = useState<AtticusForm[] | undefined>(
    undefined
  );
  const [estateFormsIsLoading, setEstateFormsIsLoading] =
    useState<boolean>(true);

  // Refetch Forms when Probate Type or Active Estate changes
  useEffect(() => {
    (async () => {
      if (activeEstate?.id) {
        setEstateFormsIsLoading(true);
        try {
          await fetchEstateForms(activeEstate.id);
        } catch (e) {
          console.error(
            "EstateFormsContextWrapper failed to fetch estate forms for estate: " +
            activeEstate.id +
            ", err=" +
            e
          );
        } finally {
          setEstateFormsIsLoading(false);
        }
      }
    })();
  }, [activeEstate?.id]);

  const fetchEstateForms = async (estateId: number): Promise<void> => {
    const fetchedEstateForms = await estateFormsApi.getEstateForms(estateId);
    setEstateForms(fetchedEstateForms);
  };

  const addEstateForm = async (
    estateId: number, 
    userId: number,
    form: AtticusForm
  ): Promise<boolean> => {
    try {
      const retForm = await estateFormsApi.addFormToEstate(estateId, userId, form);
      if (retForm) {
        setEstateForms([...(estateForms || []), form]);
        return true
      }
    } catch (e) {
      console.error("EstateForm addition failed for estate: " + estateId + " for user: " + userId + " for form: " + form.id + " with error: " + e);
    }
    return false
  };

  const removeEstateForm = async (
    estateId: number,
    userId: number,
    form: AtticusForm
  ): Promise<boolean> => {
    try {
      if (await estateFormsApi.removeFormFromEstate(estateId, userId, form)) {
        setEstateForms(
          (estateForms || []).filter((estateForm) => estateForm.id !== form.id)
        );
        return true
      } else {
        console.log("EstateForm removal failed for estate: " + estateId + " for user: " + userId + " for form: " + form.id);
      }
    } catch (e) {
      console.error("EstateForm removal failed for estate: " + estateId + " for user: " + userId + " for form: " + form.id + " with error: " + e);
    }
    return false
  };

  return (
    <EstateFormsContext.Provider
      value={{
        estateForms,
        fetchEstateForms,
        addEstateForm,
        removeEstateForm,
        estateFormsIsLoading,
      }}
    >
      {children}
    </EstateFormsContext.Provider>
  );
};
